/**
 * PublishToPanel — Patch 33.2.1.
 * Reusable channel picker + publish-mode selector. Consumed by the
 * Universal Asset Editor + any future composer. Talks only to the
 * provider-neutral Publishing Engine API — no "Zernio" awareness.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Calendar, CheckCircle2, ExternalLink, Loader2, Save, Send } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Skeleton } from "@/shared/ui/skeleton";
import { toast } from "sonner";

import {
  LAUNCH_PLATFORMS,
  PublishingChannelsAPI,
  type ChannelsResponse,
  type PublishPayload,
  type PublishResult,
} from "@/entities/channels";

interface PublishToPanelProps {
  workspaceId: string;
  assetId?: string | null;
  body: string;
  disabled?: boolean;
  onPublished?: (result: PublishResult) => void;
}

type PublishMode = "draft" | "publish_now" | "schedule";

const TIMEZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/New_York",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Australia/Sydney",
];

export function PublishToPanel({ workspaceId, assetId, body, disabled, onPublished }: PublishToPanelProps): JSX.Element {
  const qc = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<PublishMode>("draft");
  const [scheduledDate, setScheduledDate] = useState<string>("");
  const [scheduledTime, setScheduledTime] = useState<string>("");
  const [timezone, setTimezone] = useState<string>("UTC");
  const [lastResult, setLastResult] = useState<PublishResult | null>(null);

  const { data, isLoading } = useQuery<ChannelsResponse>({
    queryKey: ["publishing", "channels", workspaceId],
    queryFn: () => PublishingChannelsAPI.listChannels(workspaceId),
    enabled: !!workspaceId,
  });

  const connectedChannels = useMemo(
    () => (data?.channels ?? []).filter((c) => c.is_connected),
    [data],
  );

  const toggleAccount = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const selectAll = () => {
    setSelectedIds(new Set(connectedChannels.map((c) => c.provider_account_id)));
  };

  const scheduledFor = useMemo(() => {
    if (mode !== "schedule" || !scheduledDate || !scheduledTime) return null;
    return new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
  }, [mode, scheduledDate, scheduledTime]);

  const validationError = useMemo((): string | null => {
    if (!body.trim()) return "Content is empty.";
    if (selectedIds.size === 0) return "Choose at least one connected channel.";
    if (mode === "schedule" && !scheduledFor) return "Pick a date and time.";
    if (mode === "schedule" && scheduledFor && new Date(scheduledFor).getTime() <= Date.now()) {
      return "Schedule must be in the future.";
    }
    return null;
  }, [body, selectedIds, mode, scheduledFor]);

  const publishMutation = useMutation({
    mutationFn: () => {
      const payload: PublishPayload = {
        provider_account_ids: Array.from(selectedIds),
        body,
        media: [],
        scheduled_for: mode === "schedule" ? scheduledFor : null,
        publish_now: mode === "publish_now",
        timezone,
        idempotency_key: `${workspaceId}-${assetId ?? "adhoc"}-${Date.now()}`,
        metadata: assetId ? { asset_id: assetId } : {},
      };
      return PublishingChannelsAPI.publish(workspaceId, payload);
    },
    onSuccess: (result) => {
      setLastResult(result);
      const label = {
        draft: "Draft saved",
        publish_now: "Sent to publisher",
        schedule: "Scheduled",
      }[mode];
      const statusLabel = result.status === "partial" ? "with partial results" : "";
      toast.success(`${label} ${statusLabel}`.trim());
      qc.invalidateQueries({ queryKey: ["publishing"] });
      onPublished?.(result);
    },
    onError: (err: Error) => {
      // Never surface raw provider payloads — the backend already normalised.
      toast.error(err.message?.slice(0, 240) || "Publish failed");
    },
  });

  const submit = () => {
    if (validationError) {
      toast.error(validationError);
      return;
    }
    publishMutation.mutate();
  };

  return (
    <Card data-testid="publish-to-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span>Publish to</span>
          {connectedChannels.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={selectAll} data-testid="publish-select-all">
              Select all
            </Button>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Channel selector */}
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : connectedChannels.length === 0 ? (
          <div
            className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground"
            data-testid="publish-panel-empty"
          >
            No connected channels yet. Connect a social account on the{" "}
            <a href="/channels" className="text-primary underline">
              Channels page
            </a>{" "}
            to publish from here.
          </div>
        ) : (
          <div className="space-y-1">
            {connectedChannels.map((c) => {
              const meta = LAUNCH_PLATFORMS.find((p) => p.key === c.platform);
              const checked = selectedIds.has(c.provider_account_id);
              return (
                <label
                  key={c.id}
                  className={`flex cursor-pointer items-center gap-3 rounded-md border p-2 text-sm hover:bg-accent/40 ${
                    checked ? "border-primary/60 bg-primary/5" : ""
                  }`}
                  data-testid={`publish-channel-${c.provider_account_id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAccount(c.provider_account_id)}
                    className="h-4 w-4 rounded border-border"
                    data-testid={`publish-channel-checkbox-${c.provider_account_id}`}
                    aria-label={`Publish to ${c.display_name ?? c.platform}`}
                  />
                  <span
                    className="inline-block h-6 w-6 rounded-full border"
                    style={{ backgroundColor: meta?.color ?? "var(--muted)" }}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">
                      {c.display_name ?? c.username ?? c.provider_account_id}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {meta?.label ?? c.platform}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    Connected
                  </Badge>
                </label>
              );
            })}
          </div>
        )}

        {/* Mode selector */}
        <fieldset className="space-y-2" aria-labelledby="publish-mode-legend">
          <legend id="publish-mode-legend" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Publish mode
          </legend>
          <div className="grid grid-cols-3 gap-2">
            {(["draft", "publish_now", "schedule"] as const).map((m) => (
              <label
                key={m}
                className={`flex cursor-pointer items-center justify-center rounded-md border px-3 py-2 text-sm ${
                  mode === m ? "border-primary bg-primary/5 font-medium" : ""
                }`}
                data-testid={`publish-mode-${m}`}
              >
                <input
                  type="radio"
                  name="publish-mode"
                  value={m}
                  checked={mode === m}
                  onChange={() => setMode(m)}
                  className="sr-only"
                />
                {m === "draft" ? "Save Draft" : m === "publish_now" ? "Publish Now" : "Schedule"}
              </label>
            ))}
          </div>
        </fieldset>

        {/* Schedule details */}
        {mode === "schedule" ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="publish-schedule-fields">
            <div>
              <Label htmlFor="publish-date">Date</Label>
              <Input
                id="publish-date"
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                data-testid="publish-schedule-date"
              />
            </div>
            <div>
              <Label htmlFor="publish-time">Time</Label>
              <Input
                id="publish-time"
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                data-testid="publish-schedule-time"
              />
            </div>
            <div>
              <Label htmlFor="publish-tz">Timezone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger id="publish-tz" data-testid="publish-schedule-timezone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : null}

        {/* Validation */}
        {validationError ? (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-amber-800 dark:text-amber-200"
            data-testid="publish-validation"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {validationError}
          </div>
        ) : null}

        {/* Submit */}
        <div className="flex justify-end">
          <Button
            onClick={submit}
            disabled={disabled || publishMutation.isPending || !!validationError}
            data-testid="publish-submit"
          >
            {publishMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : mode === "draft" ? (
              <Save className="mr-2 h-4 w-4" />
            ) : mode === "schedule" ? (
              <Calendar className="mr-2 h-4 w-4" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            {mode === "draft" ? "Save Draft" : mode === "schedule" ? "Schedule" : "Publish Now"}
          </Button>
        </div>

        {/* Result */}
        {lastResult ? (
          <div className="space-y-2 rounded-md border p-3" data-testid="publish-result">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Result</span>
              <Badge variant="outline">{lastResult.status}</Badge>
            </div>
            <div className="space-y-1">
              {lastResult.per_account_results.map((r) => (
                <div
                  key={r.provider_account_id}
                  className="flex items-center justify-between gap-2 rounded border p-2 text-xs"
                  data-testid={`publish-per-account-${r.provider_account_id}`}
                >
                  <div className="flex items-center gap-2">
                    {r.status === "published" || r.status === "scheduled" || r.status === "draft" ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-rose-600" />
                    )}
                    <span className="font-medium capitalize">{r.platform}</span>
                    <span className="text-muted-foreground">— {r.status}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.external_url ? (
                      <a
                        href={r.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                        data-testid={`publish-view-post-${r.provider_account_id}`}
                      >
                        View Post <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : null}
                    {r.external_url ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(r.external_url!);
                          toast.success("Link copied");
                        }}
                        data-testid={`publish-copy-link-${r.provider_account_id}`}
                      >
                        Copy Link
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
